/**
 * Colony city-view background — a real Mars terrain photo, replacing
 * colony-city-renderer.ts's procedural drawTerrainBackground (a flat
 * fill + gradient blobs + noise, borrowed from worldmap-renderer.ts, where
 * it exists only as a fallback while the world map's own real image loads —
 * the colony screen never had a real background image at all until now).
 *
 * Ships two sizes, same convention as every other asset this session
 * (building-art.ts's -1k/-2k pair): `colonyBackgroundSrcFor` picks the 1k
 * variant for a small/narrow canvas (mobile-first — never make a phone
 * download a 2048px image) and the 2k variant otherwise. Both are preloaded
 * up front so switching viewport size mid-session (resize, rotate) never
 * triggers a fresh network request.
 */

const SRC_1K = '/colony-bg-1k.webp';
const SRC_2K = '/colony-bg-2k.webp';

/** Canvas pixel width (post-devicePixelRatio) at or below which the smaller 1k asset is used. */
const SIZE_SWITCH_WIDTH_PX = 1024;

export function colonyBackgroundSrcFor(canvasWidthPx: number): string {
  return canvasWidthPx <= SIZE_SWITCH_WIDTH_PX ? SRC_1K : SRC_2K;
}

const backgroundImageCache = new Map<string, HTMLImageElement>();
const backgroundImageLoading = new Map<string, Promise<HTMLImageElement>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = src;
  });
}

/** Already-loaded background image for the size this canvas width picks, if any — a synchronous, cache-only lookup. */
export function loadedColonyBackgroundImage(canvasWidthPx: number): HTMLImageElement | undefined {
  return backgroundImageCache.get(colonyBackgroundSrcFor(canvasWidthPx));
}

/**
 * Kicks off loading both sizes that aren't already cached, and calls
 * `onLoaded` once each becomes available. No retry on failure — a size
 * simply never becomes available in the cache, and drawCityScene already
 * treats an unavailable background as "fall back to the procedural terrain",
 * same convention as wall-art.ts/building-art.ts.
 */
export function preloadColonyBackground(onLoaded: (src: string) => void): void {
  for (const src of [SRC_1K, SRC_2K]) {
    if (backgroundImageCache.has(src)) continue;

    const inFlight = backgroundImageLoading.get(src);
    if (inFlight) {
      inFlight.then(() => onLoaded(src)).catch(() => {});
      continue;
    }

    const promise = loadImage(src);
    backgroundImageLoading.set(src, promise);
    promise
      .then((img) => {
        backgroundImageCache.set(src, img);
        onLoaded(src);
      })
      .catch(() => {
        /* no fallback — see doc comment above */
      })
      .finally(() => backgroundImageLoading.delete(src));
  }
}

/** Test seam — the cache is process-wide and would leak between cases. */
export function clearColonyBackgroundCache(): void {
  backgroundImageCache.clear();
  backgroundImageLoading.clear();
}
